import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UserData, UserDataService } from '@app/discoverer/app-core';
import { DiscovererQueryService, IDRFilter } from '@app/discoverer/core';
import { DiscoReportContext } from '@app/discoverer/dynamic-reports';

import {
    BehaviorSubject,
    Observable,
    Subject,
    combineLatest,
    interval,
    timer,
} from 'rxjs';
import {
    distinctUntilChanged,
    map,
    shareReplay,
    switchMap,
    takeUntil,
} from 'rxjs/operators';
import { HOME_ANIMATIONS } from '../config/home.animations';
import { HOME_CONSTANTS } from '../config/home.constants';
import { HERO_FEATURES } from '../config/hero-features.data';
import { HeroFeature } from '../interfaces/hero-feature.interface';
import { DRILL_OUT_CUSTOMER_CHART_CONFIGURATIONS, CUSTOMER_TABLES_COMNFIGURATION } from './drill-out-customer-chart.data';
import { DynFormComponent } from '@app/discoverer/dynamic-forms/dyn-forms/dyn-form/dyn-form.component';
import { FormGroup } from '@angular/forms';
import { BlendData } from '@app/drill-out/models/blend-data';
import { CustomerSheet } from '@app/drill-out/models/customer-sheet';
import { HttpClient } from '@angular/common/http';
import { ChartConfigVM, TableConfigVM } from '@app/shared-components/chart.interfaces';
import { AggregatedTableConfig } from '@app/shared-components/custom-components/aggregated-table/aggregate-table.configs';
import { PDFExportComponent } from '@progress/kendo-angular-pdf-export';

interface FilterState {
    drillOutTemplateId: string | null;
    date: {
        startDate: Date | null;
        endDate: Date | null;
    };
}
@Component({
    selector: 'home-page',
    templateUrl: './drill-out-customer-home-component.html',
    styleUrls: ['./drill-out-customer-home-component.scss'],
    animations: HOME_ANIMATIONS,
    providers: [
    ]
})
export class DrillOutCustomerHomeComponent implements OnInit, OnDestroy {



    private readonly destroy$ = new Subject<void>();
    public selectedDate: Date = new Date();
    public loading = false;
    public HOME_CONSTANTS = HOME_CONSTANTS;
    public reportContext!: DiscoReportContext;
    public heroFeatures: HeroFeature[] = HERO_FEATURES;
    public currentFeatureIndex = 0;
    public chartOptions: ChartConfigVM[]
    public tableOptions: TableConfigVM[]
    readonly hexCount = 15;
    readonly hexSlots = Array.from({ length: this.hexCount });
    trackByIndex = (i: number) => i;
    readonly particleCount = 12;
    readonly particleSlots = Array.from({ length: this.particleCount });
    particleTrackByIndex = (i: number) => i;
    currentDate = new Date();

    @ViewChild('pdf') pdf: PDFExportComponent;
    pdfScale = 0.75;
    isExporting = false;

    newFilters: FilterState = {
        drillOutTemplateId: null,
        date: {
            startDate: null,
            endDate: null
        }
    }
    @ViewChild('dynForm') dynForm: DynFormComponent;
    form = new FormGroup({});
    data = {}
    cards = [
    ];

    constructor(
        private readonly userDataService: UserDataService,
        private http: HttpClient
    ) {
        this.chartOptions = DRILL_OUT_CUSTOMER_CHART_CONFIGURATIONS;
        this.tableOptions = CUSTOMER_TABLES_COMNFIGURATION;

    }

    async ngOnInit(): Promise<void> {

        timer(HOME_CONSTANTS.ANIMATION_TIMINGS.FEATURE_START_DELAY)
            .pipe(
                switchMap(() =>
                    interval(HOME_CONSTANTS.ANIMATION_TIMINGS.FEATURE_INTERVAL)
                ),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.currentFeatureIndex =
                    (this.currentFeatureIndex + 1) % this.heroFeatures.length;
            });


    }
    public onFilters(event) {
        if (event) {
            this.newFilters = {
                ...this.newFilters,
                drillOutTemplateId: event.drillOutTemplateId,
                ...(event.startDate && event.endDate && {
                    date: {
                        startDate: event.startDate,
                        endDate: event.endDate
                    }
                })
            };
            this.prepareAggCards();
        }
    }
    async exportToPDF() {
        if (!this.newFilters.drillOutTemplateId) {
            alert('Please select a drill out template first');
            return;
        }

        this.isExporting = true;

        setTimeout(() => {
            const fileName = `drilling-report-${new Date().toISOString().split('T')[0]}.pdf`;
            this.pdf.saveAs(fileName);
            this.isExporting = false;
        }, 500);
    }
    private prepareAggCards() {
        const { start, end } = this.fixDateRange(
            this.newFilters.date.startDate,
            this.newFilters.date.endDate
        );
        const fluidConfig: AggregatedTableConfig<CustomerSheet> = {
            data$: this.http.post<CustomerSheet[]>(
                '/api/drill-out/customer-sheet/by-filters',
                { drillOutTemplateId: this.newFilters.drillOutTemplateId, start, end }
            ),
            dayKey: s => s.date,
            baseHeaders: ['Date'],
            fixedColumns: [
                { key: 'ViscosityIn', header: 'ViscosityIn', selector: s => s.dischargeViscosity, aggregation: 'avg', decimals: 2 },
                { key: 'WeightIn', header: 'WeightIn', selector: s => s.dischargeMudWeight, aggregation: 'avg', decimals: 2 },
                { key: 'ViscosityOut', header: 'ViscosityOut', selector: s => s.returnViscosity, aggregation: 'avg', decimals: 2 },
                { key: 'WeightOut', header: 'WeightOut', selector: s => s.returnMudWeight, aggregation: 'avg', decimals: 2 },
            ],
        };

        const blendsConfig: AggregatedTableConfig<BlendData> = {
            data$: this.http.post<BlendData[]>(
                '/api/drill-out/blend-data/by-filters',
                { drillOutTemplateId: this.newFilters.drillOutTemplateId, start, end }
            ),
            dayKey: b => b.date,
            baseHeaders: ['Date'],
            pivot: {
                meta$: this.http.get<any[]>('/api/options/get-options/34').pipe(
                    map(opts => opts.map(o => ({ id: o.id, header: o.description })))
                ),
                idSelector: row => row.batchTypeId as any,
                valueSelector: row => row.volume ?? 0,
                aggregation: 'sum',
                decimals: 2,
                showZeroAsBlank: true
            }
        };

        this.cards = [
            { id: 'fluid', title: 'Fluid Quality', subtitle: 'Daily Averages Per Template', config: fluidConfig },
            { id: 'blends', title: 'Mud Blends', subtitle: 'Volumes By Blend Type', config: blendsConfig },
        ];
    }

    private fixDateRange(startDate: Date | null, endDate: Date | null): { start: Date | null; end: Date | null } {
        if (!startDate || !endDate) {
            return { start: null, end: null };
        }
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);

        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);

        return { start, end };
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }


}
